import { CircularProgress, Divider, Grid2 } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import type { EChartsInstance } from "echarts-for-react";
import { get, isNil } from "lodash-es";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";
import { ErrorModal } from "../../components/modals/error-modal";
import { HelmetDexteel } from "../../helmet/HelmetDexteel";
import { getError } from "../../utils";
import { useTrendingContextV2 } from "./components/chart/context/TrendingContextV2";
import { useDebouncedValue } from "./components/chart/hooks/useDebouncedValue";
import { useSeriesMinMax } from "./components/chart/hooks/useSeriesMinMax";
import type {
	CustomOptions,
	View,
	ViewTag,
	ViewTagDataSet,
	ViewTagsMap,
} from "./components/chart/models/TrendingModelsV2";
import {
	getViewTags,
	getViews,
} from "./components/chart/repository/TrendingRepositoryV2";
import { HeaderSectionV2 } from "./components/chart/sections/HeaderSectionV2";
import { TagsTableV2 } from "./components/chart/sections/TagsTableV2";
import { TrendingChartV2 } from "./components/chart/sections/TrendingChartV2";
import { getViewFromLocal } from "./components/chart/utils/LocalStorageUtils";
import type { EnvelopeBand } from "./components/chart/utils/aggregateSeries";
import { getRandomColor } from "./components/chart/utils/tagColors";
import {
	type AggregationMode,
	type ZoomRange,
	readTrendingUrlState,
	writeTrendingUrlState,
} from "./components/chart/utils/trendingUrlState";

// React Query hooks for data fetching
export const useSearchViews = ({
	autoRefresh = false,
}: {
	autoRefresh?: boolean;
}) => {
	return useQuery({
		queryKey: ["views-v2"] as const,
		queryFn: ({ signal }) => getViews({ Search: null }, signal),
		refetchInterval: autoRefresh ? 10_000 : false,
	});
};

export const useSearchViewTags = ({ viewId }: { viewId: number | null }) => {
	return useQuery({
		queryKey: ["viewTags-v2", viewId] as const,
		queryFn: ({ signal }) => {
			if (viewId) {
				return getViewTags({ viewId }, signal);
			}
			return [];
		},
		enabled: viewId !== null,
	});
};

interface BandPoint {
	timestamp: number;
	min: number;
	max: number;
}

interface SeriesQueryResult {
	series: ViewTagDataSet[][];
	rawCounts: number[];
	shownCounts: number[];
	bands: BandPoint[][];
	gapTimestamps: number[][];
}

const EMPTY_SERIES_RESULT: SeriesQueryResult = {
	series: [],
	rawCounts: [],
	shownCounts: [],
	bands: [],
	gapTimestamps: [],
};

export const useSearchSeries = ({
	start,
	end,
	tagIds,
	aggregationMode,
	rawData,
	sampleCount,
	autoRefresh = false,
}: {
	start: number;
	end: number;
	tagIds: number[];
	aggregationMode: AggregationMode;
	rawData: boolean;
	sampleCount: number;
	autoRefresh?: boolean;
}) => {
	return useQuery<SeriesQueryResult, Error>({
		queryKey: [
			"series-v2",
			start,
			end,
			tagIds,
			rawData ? "raw" : aggregationMode,
		],
		queryFn: async ({ signal }) => {
			try {
				if (!tagIds || tagIds.length === 0) {
					return EMPTY_SERIES_RESULT;
				}

				const resp = await axios.post(
					"/series?nodeName=null",
					{
						start,
						end: end || new Date().getTime(),
						tagIds,
						sampleCount: rawData ? 0 : sampleCount,
						aggregationMode,
					},
					{
						signal,
					},
				);

				return {
					series: get(resp, "data.series", []) as ViewTagDataSet[][],
					rawCounts: get(resp, "data.rawCounts", []) as number[],
					shownCounts: get(resp, "data.shownCounts", []) as number[],
					bands: (get(resp, "data.bands", null) ?? []) as BandPoint[][],
					gapTimestamps: (get(resp, "data.gapTimestamps", null) ??
						[]) as number[][],
				};
			} catch (error: any) {
				if (axios.isCancel(error) || error.name === "AbortError") {
					return EMPTY_SERIES_RESULT;
				}
				throw new Error(getError(error));
			}
		},
		refetchInterval: autoRefresh ? 10_000 : false,
		enabled: tagIds.length > 0,
		keepPreviousData: true,
	});
};

const URL_WRITE_DEBOUNCE_MS = 300;
const SERIES_FETCH_DEBOUNCE_MS = 400;

const DEFAULT_CHART_OPTIONS: CustomOptions = {
	showGridLines: true,
	smoothLines: false,
	steppedLines: false,
	showLegend: false,
	showTooltip: true,
	combinedView: true,
	showToolbox: true,
	showYAxisNames: false,
	rawData: false,
};

export const TrendingsPageV2 = () => {
	const [initialUrlState] = useState(readTrendingUrlState);
	const [autoRefresh, setAutoRefresh] = useState(initialUrlState.autoRefresh);
	const [chartOptions, setChartOptions] = useState<CustomOptions>(
		initialUrlState.customOptions ?? DEFAULT_CHART_OPTIONS,
	);
	const [aggregationMode, setAggregationMode] = useState<AggregationMode>(
		initialUrlState.aggregationMode ?? "avg",
	);
	const [zoom, setZoom] = useState<ZoomRange | null>(initialUrlState.zoom);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const tablePanelRef = usePanelRef();
	const [error, setError] = useState("");
	const [viewId, setViewId] = useState<number | null>(null);
	const [chartInstance, setChartInstance] = useState<EChartsInstance | null>(
		null,
	);
	const [dataLoadedTrigger, setDataLoadedTrigger] = useState(0);

	const {
		state: {
			viewSelected,
			viewTags,
			timeScopeStart,
			timeScopeEnd,
			scope,
			cursor1Time,
			cursor2Time,
			chartPlotWidth,
		},
		actions: {
			setViews,
			setViewTagsAndRefetch,
			setViewSelected,
			setSeriesMinMax,
			resetCursors,
		},
	} = useTrendingContextV2();
	const initialViewSelectionResolvedRef = useRef(false);
	const prevViewIdRef = useRef<number | null>(null);

	// Fetch views
	const {
		data: views,
		isLoading: viewsLoading,
		isError: viewsIsError,
		error: viewsError,
		isSuccess: viewSuccess,
	} = useSearchViews({ autoRefresh });

	// Fetch view tags
	const {
		data: viewTagsData,
		isLoading: viewTagsLoading,
		isFetching: viewTagsFetching,
		isError: viewTagsIsError,
		error: viewTagsError,
		isSuccess: viewTagsSuccess,
	} = useSearchViewTags({ viewId });

	// Memoize tagIds for series query
	// Tags are sorted in DESCENDING order to match TrendingChartV2 processing
	const tagIds = useMemo(
		() =>
			Object.values(viewTags)
				.filter((tag) => tag && tag.viewTag)
				.sort((a, b) => b.order - a.order) // DESCENDING order - MUST match TrendingChartV2 sort!
				.map((tag) => tag.viewTag.TagId),
		[viewTags],
	);

	// Real tag IDs (excluding bit-extracted synthetic IDs) for API queries
	// Bit-extracted tags derive their data client-side from source tags
	const realTagIds = useMemo(
		() =>
			Object.values(viewTags)
				.filter((tag) => tag && tag.viewTag && !tag.viewTag.IsBitExtracted)
				.sort((a, b) => b.order - a.order)
				.map((tag) => tag.viewTag.TagId),
		[viewTags],
	);

	// Stable query key that only changes on time scope or when explicitly refetching (add/zoom/pan)
	// This prevents refetch when removing tags from viewTags
	// IMPORTANT: Uses realTagIds (excludes bit-extracted tags) for API queries
	const stableTagIdsRef = useRef<number[]>([]);
	const prevTimeScopeRef = useRef<string>("");
	const queryTagIds = useMemo(() => {
		const currentTimeScope = `${timeScopeStart.getTime()}-${timeScopeEnd.getTime()}`;
		const timeScopeChanged = currentTimeScope !== prevTimeScopeRef.current;

		// Check if any new REAL tags were added (not present in stable ref)
		// Bit-extracted tags don't trigger API refetch since they derive data client-side
		const hasNewTags = realTagIds.some(
			(id) => !stableTagIdsRef.current.includes(id),
		);

		// Update query tagIds if:
		// 1. It's empty (first load)
		// 2. New REAL tags were added (not just length change)
		// 3. Time scope changed (zoom/pan) - use current realTagIds to avoid fetching removed tags
		if (
			stableTagIdsRef.current.length === 0 ||
			hasNewTags ||
			timeScopeChanged
		) {
			stableTagIdsRef.current = realTagIds;
			prevTimeScopeRef.current = currentTimeScope;
		}
		return stableTagIdsRef.current;
	}, [realTagIds, timeScopeStart, timeScopeEnd]);

	const fetchWindow = useMemo(() => {
		const start = timeScopeStart.getTime();
		const end = timeScopeEnd.getTime();

		if (chartOptions.rawData || !zoom) {
			return { start, end };
		}

		const span = end - start;
		return {
			start: Math.round(start + (span * zoom.start) / 100),
			end: Math.round(start + (span * zoom.end) / 100),
		};
	}, [timeScopeStart, timeScopeEnd, zoom, chartOptions.rawData]);

	const settledFetchWindow = useDebouncedValue(
		fetchWindow,
		SERIES_FETCH_DEBOUNCE_MS,
	);

	const fetchPending =
		fetchWindow.start !== settledFetchWindow.start ||
		fetchWindow.end !== settledFetchWindow.end;

	// Fetch series data using stable query key
	const {
		data: seriesResponse,
		isLoading: seriesLoading,
		isFetching: seriesFetching,
		isError: seriesIsError,
		error: seriesError,
	} = useSearchSeries({
		start: settledFetchWindow.start,
		end: settledFetchWindow.end,
		tagIds: queryTagIds,
		aggregationMode,
		rawData: chartOptions.rawData,
		sampleCount: chartPlotWidth,
		autoRefresh,
	});

	const series = seriesResponse?.series;

	const seriesRawCounts = useMemo(() => {
		const map: Record<number, number> = {};
		const rawCounts = seriesResponse?.rawCounts;
		if (!rawCounts) return map;

		queryTagIds.forEach((tagId, index) => {
			map[tagId] = rawCounts[index] ?? 0;
		});
		return map;
	}, [seriesResponse, queryTagIds]);

	const seriesShownCounts = useMemo(() => {
		const map: Record<number, number> = {};
		const shownCounts = seriesResponse?.shownCounts;
		if (!shownCounts) return map;

		queryTagIds.forEach((tagId, index) => {
			map[tagId] = shownCounts[index] ?? 0;
		});
		return map;
	}, [seriesResponse, queryTagIds]);

	const seriesGaps = useMemo(() => {
		const map: Record<number, ReadonlySet<number>> = {};
		const gapTimestamps = seriesResponse?.gapTimestamps;
		if (!gapTimestamps || gapTimestamps.length === 0) return map;

		queryTagIds.forEach((tagId, index) => {
			const stamps = gapTimestamps[index];
			if (!stamps || stamps.length === 0) return;

			map[tagId] = new Set(stamps);
		});
		return map;
	}, [seriesResponse, queryTagIds]);

	const seriesBands = useMemo(() => {
		const map: Record<number, EnvelopeBand> = {};
		const bands = seriesResponse?.bands;
		if (!bands || bands.length === 0) return map;

		queryTagIds.forEach((tagId, index) => {
			const points = bands[index];
			if (!points || points.length === 0) return;

			map[tagId] = {
				min: points.map((p) => [p.timestamp, p.min] as [number, number | null]),
				max: points.map((p) => [p.timestamp, p.max] as [number, number | null]),
			};
		});
		return map;
	}, [seriesResponse, queryTagIds]);

	// Filter and reorder series to match current viewTags order
	// Also handles bit-extracted tags by deriving their data from source tags
	const filteredSeries = useMemo(() => {
		if (!series) return [];

		// IMPORTANT: Ensure series data matches queryTagIds before mapping
		// If lengths don't match, the series data is stale (from a previous query)
		if (series.length !== queryTagIds.length) {
			return [];
		}

		// Create a map of tagId -> series data from queryTagIds order
		const seriesMap = new Map<number, ViewTagDataSet[]>();
		queryTagIds.forEach((tagId, index) => {
			if (series[index]) {
				seriesMap.set(tagId, series[index]);
			}
		});

		// Build viewTags array sorted by order (descending) to match tagIds order
		const viewTagsArray = Object.values(viewTags)
			.filter((tag) => tag && tag.viewTag)
			.sort((a, b) => b.order - a.order);

		// Return series in the SAME order as tagIds (which matches viewTags sort order)
		// For bit-extracted tags, derive data from source tag by extracting the specific bit
		return tagIds
			.map((tagId, idx) => {
				const viewTagEntry = viewTagsArray[idx];
				if (!viewTagEntry) return null;

				const viewTag = viewTagEntry.viewTag;

				// If this is a bit-extracted tag, derive data from source
				if (
					viewTag.IsBitExtracted &&
					viewTag.SourceTagId !== undefined &&
					viewTag.BitIndex !== undefined
				) {
					const sourceData = seriesMap.get(viewTag.SourceTagId);
					if (!sourceData) return null;

					// Extract bit from each data point
					// BitIndex 0 = LSB (least significant bit)
					return sourceData.map((point) => ({
						timestamp: point.timestamp,
						value:
							point.value !== null
								? (Math.floor(point.value) >> viewTag.BitIndex!) & 1
								: null,
					}));
				}

				// Regular tag - use series data directly
				return seriesMap.get(tagId);
			})
			.filter(Boolean) as ViewTagDataSet[][];
	}, [series, tagIds, queryTagIds, viewTags]);

	// Calculate overall min/max values from filtered series data
	const seriesMinMaxData = useSeriesMinMax(filteredSeries, tagIds);

	// Load initial view when views are fetched
	useEffect(() => {
		if (!viewSuccess || !views || !Array.isArray(views)) return;

		// Always update the views list in context
		setViews(views as View[]);

		// Only set initial view if none is selected
		if (!viewSelected) {
			const initialViewId = initialUrlState.viewId;
			if (!initialViewSelectionResolvedRef.current && initialViewId !== null) {
				initialViewSelectionResolvedRef.current = true;
				const urlView = (views as View[]).find(
					(view) => view.ViewId === initialViewId,
				);

				if (urlView) {
					prevViewIdRef.current = urlView.ViewId;
					setViewId(urlView.ViewId);
					setViewSelected(urlView);
					return;
				}
			}

			const lastCreated = (views as View[]).reduce(
				(prev: View, current: View) =>
					prev && prev.ViewId > current.ViewId ? prev : current,
				null as any,
			);
			if (lastCreated) {
				initialViewSelectionResolvedRef.current = true;
				prevViewIdRef.current = lastCreated.ViewId;
				setViewId(lastCreated.ViewId);
				setViewSelected(lastCreated);
			}
		} else {
			initialViewSelectionResolvedRef.current = true;
		}
	}, [
		viewSuccess,
		views,
		viewSelected,
		setViews,
		setViewSelected,
		initialUrlState,
	]);

	// Update viewId when viewSelected changes (e.g., from LoadViewModal)
	useEffect(() => {
		if (viewSelected) {
			setViewId(viewSelected.ViewId);
		}
	}, [viewSelected]);

	useEffect(() => {
		if (!initialViewSelectionResolvedRef.current) return;

		const writeTimer = setTimeout(() => {
			writeTrendingUrlState({
				viewId: viewSelected ? viewSelected.ViewId : null,
				timeScope: {
					start: timeScopeStart.getTime(),
					end: timeScopeEnd.getTime(),
					period: scope,
				},
				zoom,
				cursors: { cursor1: cursor1Time, cursor2: cursor2Time },
				customOptions: chartOptions,
				aggregationMode,
				autoRefresh,
			});
		}, URL_WRITE_DEBOUNCE_MS);

		return () => clearTimeout(writeTimer);
	}, [
		viewSelected,
		timeScopeStart,
		timeScopeEnd,
		scope,
		zoom,
		cursor1Time,
		cursor2Time,
		chartOptions,
		aggregationMode,
		autoRefresh,
	]);

	// Load view tags when fetched - ONLY when viewId changes, not on every data refetch
	// This preserves local order changes (from drag-and-drop) until a new view is loaded
	const loadedViewIdRef = useRef<number | null>(null);
	useEffect(() => {
		if (
			!viewTagsSuccess ||
			!viewTagsData ||
			!Array.isArray(viewTagsData) ||
			viewId === null
		)
			return;

		// Only reset viewTags when viewId actually changes (loading a different view)
		// Do NOT reset if viewId is the same (data is just refetching)
		if (viewId === loadedViewIdRef.current) return;

		loadedViewIdRef.current = viewId;

		const backendTags = viewTagsData as ViewTag[];
		const localViewTags = getViewFromLocal(viewId);

		let mergedTags: ViewTagsMap = {};

		if (localViewTags) {
			// If local storage exists, use it as the source of truth for metadata (Order, Alias)
			// and for virtual tags (Bit Extracted)
			const backendTagIds = new Set(backendTags.map((t) => t.TagId));

			// 1. Process local tags
			Object.values(localViewTags).forEach((entry) => {
				const { viewTag } = entry;

				// If it's a backend tag, check if it still exists in the backend response
				// (Permission check or tag deletion check)
				if (!viewTag.IsBitExtracted) {
					if (backendTagIds.has(viewTag.TagId)) {
						mergedTags[viewTag.TagId] = entry;
					}
				} else {
					// Virtual tags are always added from local storage
					mergedTags[viewTag.TagId] = entry;
				}
			});

			// 2. Add any new backend tags that might be missing from local storage
			// (Rare case, but possible if added by another user/session)
			backendTags.forEach((backendTag) => {
				if (!mergedTags[backendTag.TagId]) {
					// Add with default order at the end
					const maxOrder = Math.max(
						...Object.values(mergedTags).map((t) => t.order),
						0,
					);
					if (isNil(backendTag.Color)) {
						backendTag.Color = getRandomColor();
					}
					mergedTags[backendTag.TagId] = {
						viewTag: backendTag,
						order: maxOrder + 1,
					};
				}
			});
		} else {
			// No local storage -> Use backend tags with default ordering
			mergedTags = backendTags.reduce(
				(carr: ViewTagsMap, curr: ViewTag, idx: number) => {
					if (isNil(curr.Color)) {
						curr.Color = getRandomColor();
					}
					carr[curr.TagId] = {
						viewTag: curr,
						order: idx,
					};
					return carr;
				},
				{},
			);
		}

		// Reset stable ref when loading new view
		stableTagIdsRef.current = [];
		setViewTagsAndRefetch(mergedTags);
	}, [viewTagsSuccess, viewTagsData, setViewTagsAndRefetch, viewId]);

	// Update context with calculated min/max values
	// Use ref to prevent infinite loop from object reference changes
	const seriesMinMaxDataRef = useRef<string>("");
	useEffect(() => {
		const serialized = JSON.stringify(seriesMinMaxData);
		if (serialized !== seriesMinMaxDataRef.current) {
			seriesMinMaxDataRef.current = serialized;
			setSeriesMinMax(seriesMinMaxData);
		}
	}, [seriesMinMaxData, setSeriesMinMax]);

	// Reset cursors when view changes or time scope changes significantly
	useEffect(() => {
		// Reset cursors when switching views or when time scope changes significantly
		if (viewSelected && viewSelected.ViewId !== prevViewIdRef.current) {
			prevViewIdRef.current = viewSelected.ViewId;
			resetCursors();
		}
	}, [viewSelected, resetCursors]);

	// Also reset cursors when time scope changes (zoom/pan)
	useEffect(() => {
		resetCursors();
	}, [timeScopeStart, timeScopeEnd, resetCursors]);

	// Increment trigger when series data finishes loading
	// This ensures cursors re-render AFTER chart has new data
	const prevSeriesLoadingRef = useRef(false);
	useEffect(() => {
		if (prevSeriesLoadingRef.current && !seriesLoading) {
			// Transition from loading to not loading - data just loaded
			setDataLoadedTrigger((prev) => prev + 1);
		}
		prevSeriesLoadingRef.current = seriesLoading;
	}, [seriesLoading]);

	// Handle errors
	useEffect(() => {
		if (viewTagsError && viewTagsIsError) {
			setError(
				viewTagsError instanceof Error
					? viewTagsError.message
					: String(viewTagsError),
			);
		}
		if (viewsError && viewsIsError) {
			setError(
				viewsError instanceof Error ? viewsError.message : String(viewsError),
			);
		}
		if (seriesError && seriesIsError) {
			setError(
				seriesError instanceof Error
					? seriesError.message
					: String(seriesError),
			);
		}
	}, [
		viewsError,
		viewsIsError,
		viewTagsError,
		viewTagsIsError,
		seriesError,
		seriesIsError,
	]);

	const handleToggleFullscreen = useCallback(() => {
		setIsFullscreen((prev) => !prev);
	}, []);

	useEffect(() => {
		if (isFullscreen) {
			tablePanelRef.current?.collapse();
		} else {
			tablePanelRef.current?.expand();
		}
	}, [isFullscreen, tablePanelRef]);

	useEffect(() => {
		if (!isFullscreen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setIsFullscreen(false);
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isFullscreen]);

	const isLoading = viewsLoading || (viewTagsLoading && viewTagsFetching);

	return (
		<>
			<HelmetDexteel title="Trendings" />
			<div
				style={{
					padding: "16px",
					width: "100%",
					height: "92vh",
					position: "relative",
					backgroundColor: "#FAFAFA",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Loading overlay */}
				{isLoading && (
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							background: "rgba(255, 255, 255, 0.7)",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							zIndex: 1000,
						}}
					>
						<CircularProgress size="3rem" />
					</div>
				)}

				{/* Header Section - Compact */}
				<div
					style={{
						flexShrink: 0,
						display: isFullscreen ? "none" : "block",
					}}
				>
					<HeaderSectionV2
						autoRefresh={autoRefresh}
						setAutoRefresh={setAutoRefresh}
						customOptions={chartOptions}
						setCustomOptions={setChartOptions}
						aggregationMode={aggregationMode}
						setAggregationMode={setAggregationMode}
						chartInstance={chartInstance}
					/>
				</div>

				<Divider sx={{ my: 2, display: isFullscreen ? "none" : "block" }} />

				{/* Resizable Area - Chart and Table */}
				<div
					style={{
						flexGrow: 1,
						minHeight: 0,
						display: "flex",
						width: "100%",
					}}
				>
					<Group
						orientation="vertical"
						style={{ width: "100%", height: "100%" }}
					>
						{/* Chart Panel */}
						<Panel defaultSize={80} minSize={20}>
							<div style={{ height: "100%", width: "100%" }}>
								<TrendingChartV2
									customOptions={chartOptions}
									series={filteredSeries}
									seriesRawCounts={seriesRawCounts}
									seriesShownCounts={seriesShownCounts}
									seriesBands={seriesBands}
									seriesGaps={seriesGaps}
									isLoading={seriesLoading && seriesFetching}
									isRefetching={
										(fetchPending || seriesFetching) &&
										!seriesLoading &&
										!autoRefresh
									}
									onChartReady={setChartInstance}
									dataLoadedTrigger={dataLoadedTrigger}
									isFullscreen={isFullscreen}
									onToggleFullscreen={handleToggleFullscreen}
									initialZoom={initialUrlState.zoom}
									onZoomChange={setZoom}
									aggregationMode={aggregationMode}
								/>
							</div>
						</Panel>

						<Separator
							style={{
								height: "10px",
								background: "#f0f0f0",
								cursor: "row-resize",
								display: isFullscreen ? "none" : "flex",
								alignItems: "center",
								justifyContent: "center",
								borderTop: "1px solid #e0e0e0",
								borderBottom: "1px solid #e0e0e0",
							}}
						>
							<div
								style={{
									width: "40px",
									height: "4px",
									backgroundColor: "#ccc",
									borderRadius: "2px",
								}}
							/>
						</Separator>

						{/* Table Panel */}
						<Panel
							defaultSize={20}
							minSize={10}
							collapsible
							collapsedSize={0}
							panelRef={tablePanelRef}
						>
							<div style={{ height: "100%", width: "100%", overflow: "auto" }}>
								<TagsTableV2 />
							</div>
						</Panel>
					</Group>
				</div>
			</div>

			<ErrorModal
				error={error}
				onHide={() => setError("")}
				size="xl"
				title="ERROR"
			/>
		</>
	);
};
